import React, { useState, useEffect, useCallback } from 'react';
import { posService } from '../../services/dataService';
import { realtimeService } from '../../services/realtimeService';

const STATUS_CONFIG = {
  pending:   { label: 'Pending',   bg: '#fee2e2', color: '#dc2626', dot: '#dc2626' },
  confirmed: { label: 'Confirmed', bg: '#dcfce7', color: '#16a34a', dot: '#16a34a' },
  cancelled: { label: 'Cancelled', bg: '#fee2e2', color: '#dc2626', dot: '#dc2626' },
};

const STATUS_NEXT       = { pending: 'confirmed' };
const STATUS_NEXT_LABEL = { pending: 'Confirm Order' };

const FILTERS = [
  { id: 'pending',   label: 'Pending' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'all',       label: 'All' },
];

const formatTime = (ts) => {
  const d = new Date(ts);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' · ' +
    d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const timeAgo = (ts) => {
  const diff = Math.floor((Date.now() - new Date(ts)) / 60000);
  if (diff < 1) return 'Just now';
  if (diff < 60) return `${diff}m ago`;
  const hrs = Math.floor(diff / 60);
  return `${hrs}h ${diff % 60}m ago`;
};

const OrdersApp = ({ restaurant }) => {
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter]     = useState('pending');
  const [updating, setUpdating] = useState(null);

  const loadOrders = useCallback(async (silent = false) => {
    if (!restaurant?.id) return;
    if (!silent) setRefreshing(true);
    const { data } = await posService.getCounterOrders(restaurant.id, { days: 2 });
    setOrders(data || []);
    setLoading(false);
    setRefreshing(false);
  }, [restaurant?.id]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Real-time: new orders + status changes
  useEffect(() => {
    if (!restaurant?.id) return;
    const newCh = realtimeService.subscribeToOrders(restaurant.id, () => loadOrders(true));
    const updCh = realtimeService.subscribeToOrderUpdates(restaurant.id, () => loadOrders(true));
    return () => {
      realtimeService.unsubscribe(newCh);
      realtimeService.unsubscribe(updCh);
    };
  }, [restaurant?.id, loadOrders]);

  // Fallback poll every 20 s
  useEffect(() => {
    const iv = setInterval(() => loadOrders(true), 20000);
    return () => clearInterval(iv);
  }, [loadOrders]);

  const updateStatus = async (order) => {
    const next = STATUS_NEXT[order.status];
    if (!next) return;
    setUpdating(order.id);
    await posService.updateStatus(order.id, next);
    setUpdating(null);
    loadOrders(true);
    realtimeService.broadcastOrderStatusUpdate(restaurant.id, order.id);
  };

  // Exclude billed and merged orders — they live in Billing & Payment history
  const unbilled = orders.filter(o => !o.invoice_number && o.status !== 'merged');
  const filtered = filter === 'all' ? unbilled : unbilled.filter(o => o.status === filter);
  const counts   = FILTERS.reduce((acc, f) => {
    acc[f.id] = f.id === 'all' ? unbilled.length : unbilled.filter(o => o.status === f.id).length;
    return acc;
  }, {});

  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        minHeight: '60vh',
      }}>
        <div style={{
          background: 'white', padding: '40px', borderRadius: '20px',
          textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
        }}>
          <div style={{
            width: '48px', height: '48px',
            border: '4px solid #FF4444', borderTop: '4px solid transparent',
            borderRadius: '50%', margin: '0 auto 20px',
            animation: 'spin 1s linear infinite',
          }} />
          <h2 style={{ color: '#333', margin: 0 }}>Loading Orders...</h2>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px 20px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: '0 0 4px 0', fontSize: '1.8rem', fontWeight: '700', background: 'linear-gradient(135deg, #FF4444, #FF6B00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Orders
          </h1>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>Today's restaurant orders · live updates</p>
        </div>
        <button
          onClick={() => loadOrders()}
          disabled={refreshing}
          style={{
            background: refreshing ? '#e5e7eb' : 'linear-gradient(135deg, #FF4444, #FF6B00)',
            color: refreshing ? '#9ca3af' : 'white',
            border: 'none', borderRadius: '10px',
            padding: '10px 20px', fontSize: '0.9rem', fontWeight: '600',
            cursor: refreshing ? 'not-allowed' : 'pointer',
            boxShadow: refreshing ? 'none' : '0 3px 10px rgba(255,68,68,0.25)',
          }}
        >
          {refreshing ? 'Refreshing...' : '↻ Refresh'}
        </button>
      </div>

      {/* Filter tabs — same pill style as waiter */}
      <div style={{
        background: 'white', borderRadius: '15px', padding: '8px',
        marginBottom: '28px', display: 'flex', gap: '6px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      }}>
        {FILTERS.map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            style={{
              flex: 1, padding: '10px 12px', border: 'none', borderRadius: '10px',
              fontSize: '0.88rem', fontWeight: '600', cursor: 'pointer',
              background: filter === f.id ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : 'transparent',
              color: filter === f.id ? 'white' : '#666',
              transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            }}
          >
            {f.label}
            {counts[f.id] > 0 && (
              <span style={{
                background: filter === f.id ? 'rgba(255,255,255,0.3)' : '#f3f4f6',
                color: filter === f.id ? 'white' : '#555',
                borderRadius: '12px', padding: '0px 7px',
                fontSize: '0.75rem', fontWeight: '700',
              }}>
                {counts[f.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 40px',
          background: 'white', borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '16px' }}>📋</div>
          <h3 style={{ color: '#333', margin: '0 0 8px 0', fontSize: '1.4rem', fontWeight: '600' }}>
            No {filter === 'all' ? '' : filter} orders
          </h3>
          <p style={{ color: '#666', margin: 0, fontSize: '1rem' }}>
            {filter === 'pending' ? 'No new orders at the moment' : `No ${filter} orders found`}
          </p>
        </div>
      ) : (
        /* Card grid — mirrors waiter layout */
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '20px',
        }}>
          {filtered.map(order => {
            const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
            const nextLabel = STATUS_NEXT_LABEL[order.status];
            const isUpdating = updating === order.id;
            const curr = restaurant?.default_currency || '₹';

            return (
              <div key={order.id} style={{
                background: 'white',
                borderRadius: '15px',
                padding: '20px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.09)',
                border: '1px solid rgba(255,68,68,0.08)',
                borderLeft: `4px solid ${sc.dot}`,
              }}>

                {/* Card header — table + amount */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '1.25rem', fontWeight: '700', color: '#333' }}>
                      {order.order_number || '—'}
                    </h3>
                    {order.restaurant_tables && (
                      <p style={{ margin: '0 0 3px 0', color: '#666', fontSize: '0.85rem', fontWeight: '600' }}>
                        {order.restaurant_tables.custom_name || `Table ${order.restaurant_tables.table_number}`}
                      </p>
                    )}
                    <p style={{ margin: 0, color: '#999', fontSize: '0.82rem' }}>
                      {formatTime(order.created_at)} · {timeAgo(order.created_at)}
                    </p>
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontSize: '1.4rem', fontWeight: '700',
                      background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
                      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                      marginBottom: '5px',
                    }}>
                      {curr}{(order.total_payable || order.total_amount || 0).toFixed(2)}
                    </div>

                    {/* Status badge */}
                    <span style={{
                      display: 'inline-block', padding: '3px 12px',
                      borderRadius: '20px', fontSize: '0.78rem', fontWeight: '700',
                      textTransform: 'uppercase',
                      background: sc.bg, color: sc.color,
                    }}>
                      {sc.label}
                    </span>
                  </div>
                </div>

                {/* Source + extra badges */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                  <span style={{
                    padding: '2px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '600',
                    background: order.order_source === 'COUNTER' ? '#eff6ff' : '#f0fdf4',
                    color: order.order_source === 'COUNTER' ? '#1d4ed8' : '#16a34a',
                    border: `1px solid ${order.order_source === 'COUNTER' ? '#bfdbfe' : '#bbf7d0'}`,
                  }}>
                    {order.order_source === 'COUNTER' ? 'Counter' : 'QR / Waiter'}
                  </span>

                  {order.invoice_number && (
                    <span style={{
                      padding: '2px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '600',
                      background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0',
                    }}>
                      {order.invoice_number}
                    </span>
                  )}

                  {order.paid_at && (
                    <span style={{
                      padding: '2px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '600',
                      background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
                    }}>
                      Paid · {order.payment_method?.toUpperCase()}
                    </span>
                  )}
                </div>

                {/* Order items — same grey box as waiter */}
                <div style={{
                  background: '#f8f9fa', borderRadius: '10px',
                  padding: '14px', marginBottom: '12px',
                }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', fontWeight: '600', color: '#333' }}>
                    Order Items:
                  </h4>
                  {(order.order_items || []).map(oi => (
                    <div key={oi.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      marginBottom: '7px', paddingBottom: '7px', borderBottom: '1px solid #e5e7eb',
                    }}>
                      <span style={{ fontWeight: '600', color: '#333', fontSize: '0.9rem' }}>
                        {oi.quantity}× {oi.menu_items?.name}
                      </span>
                      <span style={{ fontWeight: '600', color: '#FF6B00', fontSize: '0.9rem' }}>
                        {curr}{((oi.unit_price || 0) * oi.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Notes — same yellow box as waiter */}
                {order.notes && (
                  <div style={{
                    background: '#fff3cd', border: '1px solid #ffeaa7',
                    borderRadius: '8px', padding: '10px', marginBottom: '12px',
                  }}>
                    <strong style={{ color: '#856404' }}>Notes: </strong>
                    <span style={{ color: '#856404', fontSize: '0.9rem' }}>{order.notes}</span>
                  </div>
                )}

                {/* Action button — full width, same as waiter */}
                {nextLabel && (
                  <button
                    onClick={() => updateStatus(order)}
                    disabled={isUpdating}
                    style={{
                      width: '100%', padding: '11px',
                      background: isUpdating ? '#e5e7eb' : 'linear-gradient(135deg, #FF4444, #FF6B00)',
                      color: isUpdating ? '#9ca3af' : 'white',
                      border: 'none', borderRadius: '10px',
                      fontSize: '0.92rem', fontWeight: '700',
                      cursor: isUpdating ? 'not-allowed' : 'pointer',
                      boxShadow: isUpdating ? 'none' : '0 4px 12px rgba(255,68,68,0.3)',
                      marginTop: '4px',
                    }}
                  >
                    {isUpdating ? 'Updating...' : nextLabel}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrdersApp;
